import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useAuth, type AccountRole } from "@/hooks/useAuth";

const HERO_IMAGE =
  "https://readdy.ai/api/search-image?query=Cinematic%20dark%20evening%20photograph%20of%20a%20clean%20black%20Toyota%20Land%20Cruiser%20parked%20on%20a%20Nairobi%20rooftop%20parking%20deck%20overlooking%20city%20lights%2C%20warm%20amber%20street%20glow%2C%20deep%20shadows%2C%20moody%20premium%20automotive%20lifestyle%20photography%2C%20shallow%20depth%20of%20field%2C%20high%20detail&width=1200&height=1500&seq=gari-auth-side-01&orientation=portrait";

export default function AuthPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/browse";
  const { signIn, signUp } = useAuth();

  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [role, setRole] = useState<AccountRole>("rider");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const inputClass =
    "w-full rounded-lg border border-background-200/70 bg-background-50 px-3 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none";

  function switchMode(next: "signin" | "signup") {
    setMode(next);
    setStatus("idle");
    setFormError("");
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError("");

    if (mode === "signup" && fullName.trim().length < 2) {
      setStatus("error");
      setFormError("Please tell us your name.");
      return;
    }
    if (password.length < 6) {
      setStatus("error");
      setFormError("Your password needs at least 6 characters.");
      return;
    }

    setStatus("loading");

    if (mode === "signin") {
      const result = await signIn(email.trim(), password);
      if (result.error) {
        setStatus("error");
        setFormError(result.error);
        return;
      }
      navigate(redirect, { replace: true });
      return;
    }

    const result = await signUp({ email: email.trim(), password, fullName: fullName.trim(), role, phone: phone.trim() });
    if (result.error) {
      setStatus("error");
      setFormError(result.error);
      return;
    }
    if (result.needsConfirmation) {
      setStatus("success");
      return;
    }
    navigate(redirect, { replace: true });
  }

  return (
    <div className="min-h-screen w-full bg-background-50">
      <Navbar />

      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-8 px-4 pb-20 pt-28 md:px-8 md:pt-36 lg:grid-cols-2">
        <div className="relative hidden overflow-hidden rounded-3xl border border-background-200/70 lg:block">
          <img
            src={HERO_IMAGE}
            alt="A car parked overlooking Nairobi at night"
            title="Join Gari — Kenya's car rental marketplace"
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <span className="inline-flex items-center gap-2 rounded-full border border-background-200 bg-background-100/70 px-3.5 py-1.5 text-xs text-foreground-700 backdrop-blur">
              <i className="ri-roadster-fill text-primary-500"></i> One account, endless roads
            </span>
            <h2 className="mt-4 font-heading text-3xl font-semibold text-foreground-950">
              Ride. Host. Earn.
            </h2>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-foreground-600">
              Book trusted cars across Kenya as a rider, or list your own car and earn as a host — all from one
              Gari account.
            </p>
          </div>
        </div>

        <div className="flex items-center">
          <div className="w-full rounded-3xl surface-card p-6 md:p-10">
            <div className="flex rounded-full bg-background-200/50 p-1">
              <button
                type="button"
                onClick={() => switchMode("signin")}
                className={`flex-1 rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${
                  mode === "signin" ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => switchMode("signup")}
                className={`flex-1 rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${
                  mode === "signup" ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                Create account
              </button>
            </div>

            <h1 className="mt-7 font-heading text-2xl font-semibold text-foreground-950">
              {mode === "signin" ? "Welcome back to Gari" : "Join Gari in seconds"}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              {mode === "signin"
                ? "Sign in to book cars, manage trips and message your host."
                : "Create your account to start booking — or listing your own car."}
            </p>

            {status === "success" ? (
              <div className="mt-7 rounded-xl border border-accent-500/30 bg-accent-500/10 p-5">
                <p className="flex items-center gap-2 text-sm font-medium text-accent-300">
                  <i className="ri-mail-check-line text-lg"></i> Almost there — check your email
                </p>
                <p className="mt-2 text-sm text-foreground-600">
                  We&apos;ve sent a confirmation link to <strong>{email}</strong>. Click it to activate your Gari
                  account, then sign in.
                </p>
                <button
                  type="button"
                  onClick={() => switchMode("signin")}
                  className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50"
                >
                  <i className="ri-login-circle-line"></i> Go to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-7 space-y-4">
                {mode === "signup" && (
                  <>
                    <div>
                      <span className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">
                        I&apos;m joining as
                      </span>
                      <div className="grid grid-cols-2 gap-3">
                        {(
                          [
                            { value: "rider", label: "A rider", note: "I want to book cars", icon: "ri-steering-2-line" },
                            { value: "host", label: "A host", note: "I want to earn", icon: "ri-hand-coin-line" },
                          ] as const
                        ).map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setRole(option.value)}
                            className={`flex flex-col gap-1 rounded-xl border p-3 text-left transition-colors ${
                              role === option.value
                                ? "border-primary-500 bg-primary-500/10"
                                : "border-background-200 bg-background-50 hover:border-primary-500/50"
                            }`}
                          >
                            <i className={`${option.icon} text-xl text-primary-500`}></i>
                            <span className="text-sm font-medium text-foreground-950">{option.label}</span>
                            <span className="text-xs text-foreground-500">{option.note}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="auth-name" className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">
                        Full name
                      </label>
                      <input
                        id="auth-name"
                        type="text"
                        value={fullName}
                        onChange={(event) => setFullName(event.target.value)}
                        placeholder="e.g. Wanjiku Mwangi"
                        autoComplete="name"
                        required
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label htmlFor="auth-phone" className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">
                        M-Pesa phone <span className="normal-case text-foreground-400">(optional)</span>
                      </label>
                      <input
                        id="auth-phone"
                        type="tel"
                        value={phone}
                        onChange={(event) => setPhone(event.target.value)}
                        placeholder="07XX XXX XXX"
                        autoComplete="tel"
                        className={inputClass}
                      />
                    </div>
                  </>
                )}

                <div>
                  <label htmlFor="auth-email" className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">
                    Email address
                  </label>
                  <input
                    id="auth-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@email.com"
                    autoComplete="email"
                    required
                    className={inputClass}
                  />
                </div>

                <div>
                  <label htmlFor="auth-password" className="mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="auth-password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="At least 6 characters"
                      autoComplete={mode === "signin" ? "current-password" : "new-password"}
                      required
                      className={`${inputClass} pr-11`}
                    />
                    <button
                      type="button"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-lg text-foreground-500"
                    >
                      <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                    </button>
                  </div>
                </div>

                {status === "error" && formError && (
                  <p className="flex items-center gap-2 rounded-lg border border-secondary-700/40 bg-secondary-950/40 px-3.5 py-3 text-sm text-secondary-300">
                    <i className="ri-error-warning-line"></i> {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i> Please wait
                    </>
                  ) : mode === "signin" ? (
                    <>
                      <i className="ri-login-circle-line"></i> Sign in
                    </>
                  ) : (
                    <>
                      <i className="ri-user-add-line"></i> Create my account
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-foreground-500">
                  By continuing you agree to Gari&apos;s friendly house rules and terms.
                </p>
              </form>
            )}

            <p className="mt-6 text-center text-sm text-foreground-600">
              {mode === "signin" ? (
                <>
                  New to Gari?{" "}
                  <button type="button" onClick={() => switchMode("signup")} className="font-medium text-primary-500 hover:text-primary-400">
                    Create an account
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <button type="button" onClick={() => switchMode("signin")} className="font-medium text-primary-500 hover:text-primary-400">
                    Sign in
                  </button>
                </>
              )}
            </p>

            <p className="mt-3 text-center text-xs text-foreground-500">
              <Link to="/browse" className="inline-flex items-center gap-1 hover:text-foreground-800">
                <i className="ri-arrow-left-line"></i> Keep browsing cars
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}